import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Button,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import configServ from "../../services/config";

const CompalintTable = ({ onEdit, isChanged }) => {
  const { company_id, user_id, is_super, is_admin } = useSelector(
    (state) => state.GlobalVariables
  );

  // Table Data & Pagination State
  const [complaintList, setComplaintList] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);

  // Filters State
  const [filters, setFilters] = useState({
    status: "",
    from_date: "",
    to_date: "",
  });

  // Assign Job Modal & Form States
  const [openAssignModal, setOpenAssignModal] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [submittingJob, setSubmittingJob] = useState(false);
  const [assignFormData, setAssignFormData] = useState({
    complaint_id: "",
    assigned_to: "",
    start_date: "",
    priority: "medium",
  });

  // View Assignment / Assessment Modal State
  const [openAssessmentModal, setOpenAssessmentModal] = useState(false);
  const [assessmentData, setAssessmentData] = useState(null);
  const [assessmentLoading, setAssessmentLoading] = useState(false);

  // Fetch Complaints
  const fetchComplaints = useCallback(async () => {
    if (!company_id) return;
    setLoading(true);

    const payload = {
      company_id: Number(company_id),
      status: filters.status || undefined,
      from_date: filters.from_date || undefined,
      to_date: filters.to_date || undefined,
      page: page + 1,
      per_page: rowsPerPage,
    };

    try {
      const res = await configServ.getComplaintList(payload);
      if (res?.status) {
        setComplaintList(res.data || []);
        setTotalRecords(res.pagination?.total || 0);
      }
    } catch (error) {
      console.error("Error fetching complaint list:", error);
    } finally {
      setLoading(false);
    }
  }, [company_id, filters, page, rowsPerPage]);

  useEffect(() => {
    fetchComplaints();
  }, [fetchComplaints, isChanged]);

  // Fetch Staff list for dropdown
  const fetchStaff = async () => {
    try {
      const res = await configServ.getStaffActiveCustomer({
        company_id,
        user_id,
        is_admin: Boolean(is_super || is_admin),
      });
      if (res?.status) setStaffList(res.data || []);
    } catch (error) {
      console.error("Error fetching staff:", error);
    }
  };

  // Open Dialog for Selected Complaint
  const handleOpenAssignModal = (complaint) => {
    setAssignFormData({
      complaint_id: complaint.id,
      assigned_to: "",
      start_date: new Date().toISOString().split("T")[0], // Today's default date
      priority: "medium",
    });
    fetchStaff();
    setOpenAssignModal(true);
  };

  const handleCloseAssignModal = () => {
    setOpenAssignModal(false);
    setAssignFormData({
      complaint_id: "",
      assigned_to: "",
      start_date: "",
      priority: "medium",
    });
  };

  const handleAssignInputChange = (e) => {
    const { name, value } = e.target;
    setAssignFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Submit Job Assignment
  const handleAssignJobSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      complaint_id: Number(assignFormData.complaint_id),
      assigned_to: Number(assignFormData.assigned_to),
      start_date: assignFormData.start_date,
      priority: assignFormData.priority,
    };

    setSubmittingJob(true);
    try {
      const res = await configServ.createJobFromComplaint(payload);
      if (res?.status) {
        alert(res?.message || "Job created successfully!");
        handleCloseAssignModal();
        fetchComplaints();
      } else {
        alert(res?.message || "Failed to create job.");
      }
    } catch (error) {
      console.error("Error assigning job:", error);
      alert("An error occurred while creating job.");
    } finally {
      setSubmittingJob(false);
    }
  };

  const handleGetComplaintAssesmentDetails = async (complaint_id) => {
    setOpenAssessmentModal(true);
    setAssessmentLoading(true);
    setAssessmentData(null);
    try {
      const res = await configServ.getComplaintAssesmentDetails({ complaint_id });
      if (res?.status) {
        setAssessmentData(res.data || null);
        return res.data || {};
      } else {
        console.error("Failed to fetch assessment details:", res?.message);
        setAssessmentData(null);
        return {};
      }
    } catch (error) {
      console.error("Error fetching assessment details:", error);
      setAssessmentData(null);
      return {};
    } finally {
      setAssessmentLoading(false);
    }
  };

  const handleCloseAssessmentModal = () => {
    setOpenAssessmentModal(false);
    setAssessmentData(null);
  };

  // Filter & Pagination handlers
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusChip = (status) => {
    const colorMap = {
      pending: "warning",
      approved: "success",
      denied: "error",
      in_progress: "info",
      closed: "default",
    };
    return (
      <Chip
        label={status?.toUpperCase()}
        color={colorMap[status] || "default"}
        size="small"
      />
    );
  };

  return (
    <Box sx={{ width: "100%", p: { xs: 1, sm: 2, md: 3 } }}>
      <Card elevation={3}>
        <CardContent>
          <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3 }}>
            Complaint List
          </Typography>

          {/* Filters Bar */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="status-filter-label">Filter Status</InputLabel>
                <Select
                  labelId="status-filter-label"
                  name="status"
                  value={filters.status}
                  label="Filter Status"
                  onChange={handleFilterChange}
                >
                  <MenuItem value=""><em>All Statuses</em></MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="denied">Denied</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From Date"
                name="from_date"
                value={filters.from_date}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To Date"
                name="to_date"
                value={filters.to_date}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3} sx={{ display: "flex", alignItems: "center" }}>
              <Button
                variant="outlined"
                color="secondary"
                fullWidth
                onClick={() => {
                  setFilters({ status: "", from_date: "", to_date: "" });
                  setPage(0);
                }}
              >
                Reset Filters
              </Button>
            </Grid>
          </Grid>

          {/* Data Table */}
          <TableContainer component={Paper} variant="outlined">
            <Table sx={{ minWidth: 650 }} size="small">
              <TableHead sx={{ backgroundColor: "#1976d2" }}>
                <TableRow>
                  <TableCell sx={{ color: "#ffffff", fontWeight: "bold" }}>S.No.</TableCell>
                  <TableCell sx={{ color: "#ffffff", fontWeight: "bold" }}>Customer Name</TableCell>
                  <TableCell sx={{ color: "#ffffff", fontWeight: "bold" }}>Site Address</TableCell>
                  <TableCell sx={{ color: "#ffffff", fontWeight: "bold" }}>Category</TableCell>
                  <TableCell sx={{ color: "#ffffff", fontWeight: "bold" }}>Title</TableCell>
                  <TableCell sx={{ color: "#ffffff", fontWeight: "bold" }}>Status</TableCell>
                  <TableCell sx={{ color: "#ffffff", fontWeight: "bold" }}>Created At</TableCell>
                  <TableCell align="center" sx={{ color: "#ffffff", fontWeight: "bold" }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      Loading complaints...
                    </TableCell>
                  </TableRow>
                ) : complaintList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No records found.
                    </TableCell>
                  </TableRow>
                ) : (
                  complaintList.map((row, index) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>{row.customer_name || "-"}</TableCell>
                      <TableCell>{row.site_address || "-"}</TableCell>
                      <TableCell>{row.job_category_name || "-"}</TableCell>
                      <TableCell>{row.title}</TableCell>
                      <TableCell>{getStatusChip(row.status)}</TableCell>
                      <TableCell>{row.created_at}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Assignment Details">
                          <IconButton
                            color="info"
                            size="small"
                            onClick={() => handleGetComplaintAssesmentDetails(row.id)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Edit Complaint">
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => onEdit && onEdit(row)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Assign Job">
                          <IconButton
                            color="secondary"
                            size="small"
                            onClick={() => handleOpenAssignModal(row)}
                            disabled={row.job_detail_id !== null} // Disable if job already assigned
                          >
                            <WorkOutlineIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalRecords}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>

      {/* Assign Job Dialog Form */}
      <Dialog open={openAssignModal} onClose={handleCloseAssignModal} maxWidth="xs" fullWidth>
        <DialogTitle>Assign Job from Complaint</DialogTitle>
        <Box component="form" onSubmit={handleAssignJobSubmit}>
          <DialogContent dividers>
            <Grid container spacing={2}>
              {/* Staff Select */}
              <Grid item xs={12}>
                <FormControl fullWidth required size="small">
                  <InputLabel id="staff-select-label">Assign To Staff</InputLabel>
                  <Select
                    labelId="staff-select-label"
                    name="assigned_to"
                    value={assignFormData.assigned_to}
                    label="Assign To Staff"
                    onChange={handleAssignInputChange}
                  >
                    <MenuItem value=""><em>Select Staff</em></MenuItem>
                    {staffList?.map((staff) => (
                      <MenuItem key={staff.id} value={staff.id}>
                        {staff.name || staff.contact_name || `Staff #${staff.id}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Start Date */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  size="small"
                  type="date"
                  label="Start Date"
                  name="start_date"
                  value={assignFormData.start_date}
                  onChange={handleAssignInputChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Priority Select */}
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel id="priority-label">Priority</InputLabel>
                  <Select
                    labelId="priority-label"
                    name="priority"
                    value={assignFormData.priority}
                    label="Priority"
                    onChange={handleAssignInputChange}
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseAssignModal} color="inherit">
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={submittingJob}
            >
              {submittingJob ? "Assigning..." : "Assign Job"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Complaint Assignment Details Dialog */}
      <Dialog
        open={openAssessmentModal}
        onClose={handleCloseAssessmentModal}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Complaint Assignment Details
        </DialogTitle>
        <DialogContent dividers>
          {assessmentLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
              <CircularProgress size={36} />
            </Box>
          ) : assessmentData ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, py: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1, borderBottom: "1px solid #f0f0f0" }}>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Name:
                </Typography>
                <Typography variant="body2" fontWeight={600} color="text.primary">
                  {assessmentData.name || "-"}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1, borderBottom: "1px solid #f0f0f0" }}>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Assigned At:
                </Typography>
                <Typography variant="body2" fontWeight={600} color="text.primary">
                  {assessmentData.assigned_at || "-"}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Type:
                </Typography>
                <Chip
                  label={(assessmentData.assignment_type || assessmentData.type || "-").toUpperCase()}
                  size="small"
                  color={assessmentData.assignment_type === "manual" ? "primary" : "secondary"}
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            </Box>
          ) : (
            <Box sx={{ py: 3, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                No assignment details found.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAssessmentModal} color="inherit">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompalintTable;